import { GIFT_CODE_TYPES, type GiftCodeType } from "@loot-os/api-contract";
import { sql } from "drizzle-orm";
import { check, index, integer, pgTable, text, timestamp, uniqueIndex } from "drizzle-orm/pg-core";

import { balanceLedger, endUsers, environments } from "./app.js";
import { tenant } from "./auth.js";

// ---- gift codes -------------------------------------------------------------------------------
// Balance codes a tenant admin generates in batches and a shopper redeems once, as a `gift_code`
// ledger credit (`redeemGiftCode`). See docs/gift-codes.md.

/**
 * Codes generated together: every one shares the type, amount, expiry and note. Writer: tenant
 * admin (`giftCodes.generate`, and `giftCodes.voidBatch` for the two `voided_*` columns). Whole row is safe
 * to return to that tenant's admins.
 */
export const giftCodeBatches = pgTable(
  "gift_code_batches",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    type: text("type", { enum: GIFT_CODE_TYPES }).$type<GiftCodeType>().notNull(),
    /** What each code credits, in integer US cents. */
    amountCents: integer("amount_cents").notNull(),
    /** Past this, no code of the batch redeems; null = never. */
    expiresAt: timestamp("expires_at", { withTimezone: true }),
    /** The admin's note: who the codes are for, the campaign. Staff-facing. */
    note: text("note").notNull().default(""),
    /** The dashboard user who generated it. */
    createdBy: text("created_by").notNull(),
    /** Set once staff voided the batch: none of its unredeemed codes redeems after. */
    voidedAt: timestamp("voided_at", { withTimezone: true }),
    voidedBy: text("voided_by"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("gift_code_batches_env_idx").on(t.environmentId, t.createdAt),
    check("gift_code_batches_amount_positive", sql`${t.amountCents} > 0`),
  ],
);

/**
 * One code, redeemable once in its environment. Writer: tenant admin (`giftCodes.generate` inserts,
 * `giftCodes.voidCode` sets the `voided_*` columns) and the shopper's redemption
 * (`POST /v1/me/gift-codes/redeem`, the `redeemed_*` columns). `code` is balance to whoever holds
 * it until redeemed, so it reaches tenant admins alone: never a viewer or editor, never /v1.
 */
export const giftCodes = pgTable(
  "gift_codes",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    batchId: text("batch_id")
      .notNull()
      .references(() => giftCodeBatches.id, { onDelete: "cascade" }),
    /** Sixteen characters, stored without the dashes the store prints (`formatGiftCode`). */
    code: text("code").notNull(),
    redeemedAt: timestamp("redeemed_at", { withTimezone: true }),
    redeemedByEndUserId: text("redeemed_by_end_user_id").references(() => endUsers.id, {
      onDelete: "set null",
    }),
    /** The `gift_code` ledger row the redemption wrote. */
    redemptionLedgerId: text("redemption_ledger_id").references(() => balanceLedger.id, {
      onDelete: "set null",
    }),
    voidedAt: timestamp("voided_at", { withTimezone: true }),
    voidedBy: text("voided_by"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("gift_codes_env_code_idx").on(t.environmentId, t.code),
    index("gift_codes_batch_idx").on(t.batchId),
    // A shopper's redemptions, and the cascade when an end user is deleted.
    index("gift_codes_redeemed_by_idx").on(t.redeemedByEndUserId),
  ],
);
